import { ThemeEvents } from '@theme/events';

/**
 * A custom element that displays a metafield of the selected variant.
 *
 * Variant metafields aren't present in the client-side variant JSON, so this
 * component refreshes itself from the server-rendered section markup that the
 * variant update carries (event.detail.data.html) — the same mechanism the
 * product price uses — updating without a page reload.
 */
class VariantMetafield extends HTMLElement {
  connectedCallback() {
    this.section = this.closest(
      '[id*="ProductInformation-"], [id*="QuickAdd-"], .shopify-section, dialog, product-card'
    );
    if (!this.section) return;
    this.section.addEventListener(ThemeEvents.variantUpdate, this.update);
  }

  disconnectedCallback() {
    this.section?.removeEventListener(ThemeEvents.variantUpdate, this.update);
  }

  /**
   * @param {import('@theme/events').VariantUpdateEvent} event - The variant update event.
   */
  update = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const html = event.detail.data.html;
    if (!html) return;

    const updated = html.querySelector(`variant-metafield[data-block-id="${this.dataset.blockId}"]`);
    if (!updated) return;

    const newContent = updated.querySelector('[ref="content"]');
    const currentContent = this.querySelector('[ref="content"]');
    if (newContent && currentContent) {
      currentContent.replaceWith(newContent);
    }

    // Hide the block when the newly selected variant has no value for the metafield.
    if (updated.hasAttribute('hidden')) {
      this.setAttribute('hidden', '');
    } else {
      this.removeAttribute('hidden');
    }
  };
}

if (!customElements.get('variant-metafield')) {
  customElements.define('variant-metafield', VariantMetafield);
}
